import { useEffect, useState, type FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { PageBody } from '../../components/Page';
import { Badge, type Tone } from '../../components/Pill';
import { useShopFile } from '../../components/ShopFileLayout';
import { StatusBanner } from '../../components/StatusBanner';
import { intlLocale } from '../../i18n/formats';
import { useLocale } from '../../i18n/LocaleProvider';
import { isShopAccessRequired, opsErrorMessage } from '../../lib/ops-api';
import {
  executePeriodBackfill,
  periodBackfillErrorMessage,
  previewPeriodBackfill,
  type PeriodBackfillExecuteResult,
  type PeriodBackfillPreview,
} from '../../lib/ops-period-backfill-api';
import {
  CLAMP_KEYS,
  EXCLUSION_KEYS,
  INELIGIBILITY_KEYS,
  OUTCOME_KEYS,
  defaultPeriod,
  errorKeyForMessage,
  executeBody,
  executeProblem,
  periodBounds,
  periodRequest,
  selectFirst,
  selectedDateRange,
  summarizeResults,
  toggleSelection,
} from '../../lib/period-backfill';
import { REASON_MIN_LENGTH } from '../../lib/shop-file';

/** A period as the API receives it (ISO bounds). */
interface Period {
  readonly from: string;
  readonly to: string;
}

const OUTCOME_TONES: Record<string, Tone> = {
  MINTED: 'green',
  REFUSED: 'amber',
  FAILED: 'red',
};

/** Re-renders every 15 s, so an expiring preview disarms the execute button on its own. */
function useNow(): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 15_000);
    return () => window.clearInterval(id);
  }, []);
  return now;
}

/**
 * Period backfill (work order item 6): scan a period for paid orders that have no invoice, look at
 * the list, and mint the chosen ones.
 *
 * The list is the daily reconciliation's own gap rule applied to the period (AC-6.3): POS sales,
 * orders the replay owns, orders waiting for the merchant, pre-install orders and invoiced orders
 * are shown as exclusions with the reason, never offered. Execute is armed only by a fresh preview
 * of the SAME period (AC-6.4) and the API re-checks every order before it mints, so a stale screen
 * can at worst produce a refusal. Customer email is off unless the operator ticks it (A15), and
 * the merchant is told which period to recheck.
 */
export function PeriodBackfillTab() {
  const { t, tCount, locale } = useLocale();
  const { shopId, file, reload, requireAccess } = useShopFile();
  const timeZone = file.identity.timezone || 'UTC';
  const now = useNow();

  // The anchor becomes known with the first preview; the picker then stops offering days before it.
  const [anchor, setAnchor] = useState<string | null>(null);
  const bounds = periodBounds(now, timeZone, anchor);
  const [fromDay, setFromDay] = useState(
    () => defaultPeriod(periodBounds(new Date(), timeZone, null)).from,
  );
  const [toDay, setToDay] = useState(
    () => defaultPeriod(periodBounds(new Date(), timeZone, null)).to,
  );

  const [preview, setPreview] = useState<PeriodBackfillPreview | null>(null);
  const [previewedPeriod, setPreviewedPeriod] = useState<Period | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [sendCustomerEmail, setSendCustomerEmail] = useState(false);
  const [reason, setReason] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState<PeriodBackfillExecuteResult | null>(
    null,
  );

  const request = periodRequest(fromDay, toDay, timeZone);
  const currentPeriod: Period = request.ok
    ? { from: request.from, to: request.to }
    : { from: '', to: '' };

  function apiError(error: unknown, fallbackKey: string): string {
    const key = errorKeyForMessage(periodBackfillErrorMessage(error));
    return key ? t(key) : opsErrorMessage(error, t(fallbackKey));
  }

  const scan = useMutation({
    mutationFn: (period: Period) => previewPeriodBackfill(shopId, period),
    onSuccess: (data, period) => {
      setPreview(data);
      setPreviewedPeriod(period);
      setSelected(new Set());
      setResult(null);
      if (data.window.anchor) setAnchor(data.window.anchor);
    },
    onError: (error) => {
      if (isShopAccessRequired(error, shopId)) requireAccess();
    },
  });

  const execute = useMutation({
    mutationFn: () => {
      if (!preview) throw new Error('No preview');
      return executePeriodBackfill(
        shopId,
        executeBody({
          previewToken: preview.previewToken,
          selected,
          reason,
          sendCustomerEmail,
        }),
      );
    },
    onSuccess: (data) => {
      setConfirming(false);
      setResult(data);
      // The token is spent in spirit: the orders it named are invoiced now. A next run starts
      // from a new preview.
      setPreview(null);
      setPreviewedPeriod(null);
      setSelected(new Set());
      setReason('');
      reload();
    },
    onError: (error) => {
      setConfirming(false);
      if (isShopAccessRequired(error, shopId)) requireAccess();
    },
  });

  const problem = executeProblem({
    preview,
    previewedPeriod,
    currentPeriod,
    selected,
    reason,
    now,
  });
  const range = preview ? selectedDateRange(preview, selected, timeZone) : null;

  function onPreview(event: FormEvent): void {
    event.preventDefault();
    if (!request.ok) return;
    scan.mutate({ from: request.from, to: request.to });
  }

  function onContinue(event: FormEvent): void {
    event.preventDefault();
    if (problem) return;
    setConfirming(true);
  }

  const shownDay = (iso: string) =>
    new Date(iso).toLocaleDateString(intlLocale(locale), { timeZone });
  const money = (amount: string, currency: string) => {
    const value = Number(amount);
    if (!currency || Number.isNaN(value)) return amount || '—';
    return value.toLocaleString(intlLocale(locale), {
      style: 'currency',
      currency,
    });
  };

  const ineligible = preview ? preview.shopIneligibility.length > 0 : false;

  return (
    <PageBody>
      <section className="card space-y-3">
        <h2 className="text-base font-semibold text-gray-900">
          {t('periodBackfill.title')}
        </h2>
        <p className="text-sm text-gray-600">{t('periodBackfill.lead')}</p>
        <form onSubmit={onPreview} className="flex flex-wrap gap-3 items-end">
          <div>
            <label htmlFor="periodBackfillFrom" className="field-label">
              {t('periodBackfill.period.from')}
            </label>
            <input
              id="periodBackfillFrom"
              type="date"
              value={fromDay}
              min={bounds.min}
              max={bounds.max}
              onChange={(event) => setFromDay(event.target.value)}
              className="field-input"
            />
          </div>
          <div>
            <label htmlFor="periodBackfillTo" className="field-label">
              {t('periodBackfill.period.to')}
            </label>
            <input
              id="periodBackfillTo"
              type="date"
              value={toDay}
              min={bounds.min}
              max={bounds.max}
              onChange={(event) => setToDay(event.target.value)}
              className="field-input"
            />
          </div>
          <button
            type="submit"
            className="btn-primary btn-sm"
            disabled={!request.ok || scan.isPending}
          >
            {scan.isPending
              ? t('periodBackfill.period.scanning')
              : t('periodBackfill.period.preview')}
          </button>
        </form>
        <p className="field-hint">
          {t('periodBackfill.period.hint', {
            days: 60,
            timeZone,
            min: shownDay(`${bounds.min}T12:00:00Z`),
          })}
        </p>
        {!request.ok && fromDay !== '' && toDay !== '' && (
          <p className="text-sm text-amber-700">{t(request.problemKey)}</p>
        )}
        {scan.isError && !isShopAccessRequired(scan.error) && (
          <StatusBanner tone="error">
            {apiError(scan.error, 'periodBackfill.errors.previewFailed')}
          </StatusBanner>
        )}
      </section>

      {result && <RunResult result={result} timeZone={timeZone} />}

      {preview && (
        <>
          <section className="card space-y-3">
            <h2 className="text-base font-semibold text-gray-900">
              {t('periodBackfill.window.title')}
            </h2>
            <p className="text-sm text-gray-700">
              {preview.window.empty
                ? t('periodBackfill.window.empty')
                : t('periodBackfill.window.scanned', {
                    from: shownDay(preview.window.from),
                    to: shownDay(preview.window.to),
                  })}
            </p>
            {preview.window.clampedBy.length > 0 && (
              <ul className="list-disc pl-5 text-sm text-gray-600 space-y-1">
                {preview.window.clampedBy.map((clamp) => (
                  <li key={clamp}>
                    {t(CLAMP_KEYS[clamp], {
                      anchor: preview.window.anchor
                        ? shownDay(preview.window.anchor)
                        : '—',
                    })}
                  </li>
                ))}
              </ul>
            )}
            {ineligible && (
              <StatusBanner tone="warning">
                <p>{t('periodBackfill.ineligible.lead')}</p>
                <ul className="list-disc pl-5 mt-1">
                  {preview.shopIneligibility.map((reasonToken) => (
                    <li key={reasonToken}>
                      {t(INELIGIBILITY_KEYS[reasonToken])}
                    </li>
                  ))}
                </ul>
              </StatusBanner>
            )}
            {preview.truncated && (
              <StatusBanner tone="warning">
                {t('periodBackfill.truncated', {
                  limit: preview.scanLimit,
                })}
              </StatusBanner>
            )}
            {!ineligible && !preview.window.empty && (
              <p className="text-sm text-gray-600">
                {tCount('periodBackfill.scanned', preview.scanned)}
              </p>
            )}
          </section>

          {!ineligible && !preview.window.empty && (
            <section className="card space-y-3">
              <h2 className="text-base font-semibold text-gray-900">
                {tCount(
                  'periodBackfill.candidates.title',
                  preview.candidates.length,
                )}
              </h2>
              {preview.candidates.length === 0 ? (
                <p className="text-sm text-gray-600">
                  {t('periodBackfill.candidates.none')}
                </p>
              ) : (
                <>
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      className="btn-secondary btn-sm"
                      onClick={() => setSelected(selectFirst(preview))}
                    >
                      {t('periodBackfill.candidates.selectFirst', {
                        max: preview.maxOrdersPerRun,
                      })}
                    </button>
                    <button
                      type="button"
                      className="btn-neutral btn-sm"
                      onClick={() => setSelected(new Set())}
                    >
                      {t('periodBackfill.candidates.clear')}
                    </button>
                    <span className="text-sm text-gray-600">
                      {t('periodBackfill.candidates.selected', {
                        count: selected.size,
                        max: preview.maxOrdersPerRun,
                      })}
                    </span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>
                            <span className="sr-only">
                              {t('periodBackfill.candidates.columns.select')}
                            </span>
                          </th>
                          <th>
                            {t('periodBackfill.candidates.columns.order')}
                          </th>
                          <th>
                            {t('periodBackfill.candidates.columns.invoiceDate')}
                          </th>
                          <th>
                            {t('periodBackfill.candidates.columns.total')}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {preview.candidates.map((candidate) => {
                          const checked = selected.has(candidate.orderId);
                          const full =
                            !checked &&
                            selected.size >= preview.maxOrdersPerRun;
                          return (
                            <tr key={candidate.orderId}>
                              <td>
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  disabled={full}
                                  aria-label={t(
                                    'periodBackfill.candidates.selectOne',
                                    { name: candidate.name },
                                  )}
                                  onChange={() =>
                                    setSelected((current) =>
                                      toggleSelection(
                                        current,
                                        candidate.orderId,
                                        preview.maxOrdersPerRun,
                                      ),
                                    )
                                  }
                                  className="w-4 h-4 rounded border-gray-300 accent-primary"
                                />
                              </td>
                              <td className="font-mono text-xs whitespace-nowrap">
                                {candidate.name}
                                {candidate.test && (
                                  <>
                                    {' '}
                                    <Badge tone="gray">
                                      {t('periodBackfill.candidates.test')}
                                    </Badge>
                                  </>
                                )}
                              </td>
                              <td className="whitespace-nowrap">
                                {shownDay(candidate.processedAt)}
                              </td>
                              <td className="whitespace-nowrap">
                                {money(candidate.total, candidate.currency)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </section>
          )}

          {preview.exclusions.length > 0 && (
            <section className="card space-y-3">
              <h2 className="text-base font-semibold text-gray-900">
                {t('periodBackfill.exclusions.title')}
              </h2>
              <p className="text-sm text-gray-600">
                {t('periodBackfill.exclusions.lead')}
              </p>
              {preview.exclusions.map((group) => (
                <details
                  key={group.reason}
                  className="border-t border-gray-100 pt-2"
                >
                  <summary className="cursor-pointer text-sm font-medium text-gray-900">
                    {t(EXCLUSION_KEYS[group.reason].title)} ·{' '}
                    {tCount('periodBackfill.exclusions.count', group.count)}
                  </summary>
                  <p className="text-sm text-gray-600 mt-1">
                    {t(EXCLUSION_KEYS[group.reason].explain)}
                  </p>
                  <p className="font-mono text-xs text-gray-700 mt-1 break-words">
                    {group.orders.map((order) => order.name).join(', ')}
                  </p>
                </details>
              ))}
            </section>
          )}

          {!ineligible && preview.candidates.length > 0 && (
            <section className="card space-y-3">
              <h2 className="text-base font-semibold text-gray-900">
                {t('periodBackfill.execute.title')}
              </h2>
              <form onSubmit={onContinue} className="space-y-3">
                <label className="flex items-start gap-2 text-sm text-gray-800">
                  <input
                    type="checkbox"
                    checked={sendCustomerEmail}
                    onChange={(event) =>
                      setSendCustomerEmail(event.target.checked)
                    }
                    className="w-4 h-4 mt-0.5 rounded border-gray-300 accent-primary"
                  />
                  <span>
                    {t('periodBackfill.execute.emailLabel')}
                    <span className="block text-xs text-gray-500">
                      {t(
                        sendCustomerEmail
                          ? 'periodBackfill.execute.emailOn'
                          : 'periodBackfill.execute.emailOff',
                      )}
                    </span>
                  </span>
                </label>
                <div>
                  <label htmlFor="periodBackfillReason" className="field-label">
                    {t('periodBackfill.execute.reasonLabel')}
                  </label>
                  <textarea
                    id="periodBackfillReason"
                    value={reason}
                    onChange={(event) => setReason(event.target.value)}
                    rows={3}
                    maxLength={500}
                    className="field-input"
                  />
                  <p className="field-hint">
                    {t('periodBackfill.execute.reasonHint', {
                      min: REASON_MIN_LENGTH,
                    })}
                  </p>
                </div>
                {problem && (
                  <p className="text-sm text-amber-700">{t(problem)}</p>
                )}
                {execute.isError && !isShopAccessRequired(execute.error) && (
                  <StatusBanner tone="error">
                    {apiError(
                      execute.error,
                      'periodBackfill.errors.executeFailed',
                    )}
                  </StatusBanner>
                )}
                <button
                  type="submit"
                  className="btn-primary btn-sm"
                  disabled={problem !== null || execute.isPending}
                >
                  {t('periodBackfill.execute.continue')}
                </button>
              </form>
            </section>
          )}
        </>
      )}

      <ConfirmDialog
        open={confirming}
        title={t('periodBackfill.confirm.title')}
        confirmLabel={
          execute.isPending
            ? t('periodBackfill.confirm.running')
            : tCount('periodBackfill.confirm.label', selected.size)
        }
        confirmDisabled={execute.isPending || problem !== null}
        onConfirm={() => execute.mutate()}
        onCancel={() => setConfirming(false)}
      >
        <div className="space-y-2">
          <p>
            {tCount('periodBackfill.confirm.body', selected.size, {
              from: range ? shownDay(`${range.from}T12:00:00Z`) : '—',
              to: range ? shownDay(`${range.to}T12:00:00Z`) : '—',
            })}
          </p>
          <p>{t('periodBackfill.confirm.numbering')}</p>
          <p>
            {t(
              sendCustomerEmail
                ? 'periodBackfill.confirm.emailOn'
                : 'periodBackfill.confirm.emailOff',
            )}
          </p>
          <p>{t('periodBackfill.confirm.notice')}</p>
        </div>
      </ConfirmDialog>
    </PageBody>
  );
}

/** What a run did, order by order, and the audit row it wrote. */
function RunResult({
  result,
  timeZone,
}: {
  readonly result: PeriodBackfillExecuteResult;
  readonly timeZone: string;
}) {
  const { t, locale } = useLocale();
  const counts = summarizeResults(result.results);
  return (
    <section className="card space-y-3">
      <StatusBanner tone={counts.FAILED > 0 ? 'warning' : 'success'}>
        {t('periodBackfill.results.summary', {
          minted: counts.MINTED,
          refused: counts.REFUSED,
          failed: counts.FAILED,
          auditId: result.auditLogId,
        })}
      </StatusBanner>
      <p className="text-sm text-gray-600">
        {t(
          result.merchantNoticeRaised
            ? 'periodBackfill.results.noticeRaised'
            : result.minted > 0
              ? 'periodBackfill.results.noticeMissing'
              : 'periodBackfill.results.noticeNone',
        )}
      </p>
      <div className="overflow-x-auto">
        <table className="data-table">
          <thead>
            <tr>
              <th>{t('periodBackfill.results.columns.order')}</th>
              <th>{t('periodBackfill.results.columns.outcome')}</th>
              <th>{t('periodBackfill.results.columns.invoice')}</th>
              <th>{t('periodBackfill.results.columns.invoiceDate')}</th>
              <th>{t('periodBackfill.results.columns.detail')}</th>
            </tr>
          </thead>
          <tbody>
            {result.results.map((row) => (
              <tr key={row.orderId}>
                <td className="font-mono text-xs">{row.orderId}</td>
                <td>
                  <Badge tone={OUTCOME_TONES[row.outcome] ?? 'gray'}>
                    {t(OUTCOME_KEYS[row.outcome])}
                  </Badge>
                </td>
                <td className="font-mono text-xs">
                  {row.formattedInvoiceNumber ?? '—'}
                </td>
                <td className="whitespace-nowrap">
                  {row.issueDate
                    ? new Date(row.issueDate).toLocaleDateString(
                        intlLocale(locale),
                        { timeZone },
                      )
                    : '—'}
                </td>
                <td className="text-xs text-gray-700 max-w-sm">
                  {row.refusal
                    ? t(EXCLUSION_KEYS[row.refusal].title)
                    : (row.error ?? '—')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
